'use client';

/**
 * TierConfigurator - Plan Tier Management for RevOps Admin
 *
 * Allows Trust Care admins to:
 * - Define plan tiers (Starter, Professional, Enterprise)
 * - Set capabilities per tier
 * - Configure user count ranges and per-user pricing
 * - Set included communication/AI credits per tier
 * - Configure volume discounts
 * - Preview pricing calculator
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronUp,
  Users, DollarSign, Zap, MessageSquare, Bot, Star,
  Calculator, Award, Loader2, CheckCircle, AlertCircle,
} from 'lucide-react';
// Use the same backend URL as platformApi.ts
const PLATFORM_BACKEND_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) || 'https://weavein-api.onrender.com';

const getAuthHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;
  if (tenantSlug) headers['X-Tenant-ID'] = tenantSlug;
  return headers;
};

// Types
type PricingModelType = 'flat_fee' | 'per_seat' | 'pmpm' | 'tiered' | 'usage_based' | 'revenue_share' | 'hybrid';

const PRICING_MODEL_OPTIONS: { value: PricingModelType; label: string; description: string }[] = [
  { value: 'flat_fee', label: 'Flat Fee', description: 'Fixed price — monthly, or a single annual fee (set Billing Interval = Annual)' },
  { value: 'per_seat', label: 'Per Seat (PUPM)', description: 'Base price + per-user/month fee' },
  { value: 'pmpm', label: 'Per Member (PMPM)', description: 'Base price + per-active-member/month fee' },
  { value: 'tiered', label: 'Tiered', description: 'Volume brackets with different unit prices' },
  { value: 'usage_based', label: 'Usage-Based', description: 'Consumption pricing (comms + AI tokens)' },
  { value: 'revenue_share', label: 'Revenue Share', description: '% of org revenue with min/cap' },
  { value: 'hybrid', label: 'Hybrid', description: 'Base + per-seat + revenue share + consumption' },
];

interface RevenueShareConfig {
  reimbursement_share_percent: number;
  grant_share_percent: number;
  employer_share_percent: number;
  other_share_percent: number;
  minimum_monthly_fee: number;
  cap_monthly_fee: number | null;
  reconciliation_frequency: string;
}

interface PricingTierBracket {
  from_qty: number;
  to_qty: number | null;
  unit_price: number;
  flat_price: number;
}

interface PlanTier {
  id: string;
  tier_code: string;
  tier_name: string;
  description: string;
  pricing_model: PricingModelType;
  base_monthly_price: number;
  per_user_price: number;
  per_member_price: number;
  annual_discount_percent: number;
  setup_fee: number;
  billing_interval: string;
  revenue_recognition?: string;
  min_users: number;
  max_users: number | null;
  pricing_tiers: PricingTierBracket[];
  revenue_share_config: RevenueShareConfig;
  trial_days: number;
  min_contract_months: number;
  capabilities: { capability: string; enabled: boolean; limit?: number }[];
  features: { feature: string; value?: string; limit?: number; unlimited?: boolean }[];
  included_communication_credits: number;
  included_ai_credits: number;
  volume_discounts: { min_users: number; discount_percent: number }[];
  display_order: number;
  is_popular: boolean;
  badge_text: string;
  is_active: boolean;
  is_default: boolean;
}

interface TierFormData {
  tier_code: string;
  tier_name: string;
  description: string;
  pricing_model: PricingModelType;
  base_monthly_price: number;
  per_user_price: number;
  per_member_price: number;
  annual_discount_percent: number;
  setup_fee: number;
  billing_interval: string;
  revenue_recognition?: string;
  min_users: number;
  max_users: number | null;
  pricing_tiers: PricingTierBracket[];
  revenue_share_config: RevenueShareConfig;
  trial_days: number;
  min_contract_months: number;
  capabilities: { capability: string; enabled: boolean; limit?: number }[];
  features: { feature: string; value?: string; limit?: number; unlimited?: boolean }[];
  included_communication_credits: number;
  included_ai_credits: number;
  volume_discounts: { min_users: number; discount_percent: number }[];
  display_order: number;
  is_popular: boolean;
  badge_text: string;
  is_active: boolean;
  is_default: boolean;
}

const EMPTY_REVENUE_SHARE: RevenueShareConfig = {
  reimbursement_share_percent: 0,
  grant_share_percent: 0,
  employer_share_percent: 0,
  other_share_percent: 0,
  minimum_monthly_fee: 0,
  cap_monthly_fee: null,
  reconciliation_frequency: 'monthly',
};

const EMPTY_FORM: TierFormData = {
  tier_code: '',
  tier_name: '',
  description: '',
  pricing_model: 'per_seat',
  base_monthly_price: 0,
  per_user_price: 0,
  per_member_price: 0,
  annual_discount_percent: 0,
  setup_fee: 0,
  billing_interval: 'monthly',
  revenue_recognition: 'monthly',
  min_users: 1,
  max_users: null,
  pricing_tiers: [],
  revenue_share_config: { ...EMPTY_REVENUE_SHARE },
  trial_days: 0,
  min_contract_months: 0,
  capabilities: [],
  features: [],
  included_communication_credits: 0,
  included_ai_credits: 0,
  volume_discounts: [],
  display_order: 0,
  is_popular: false,
  badge_text: '',
  is_active: true,
  is_default: false,
};

// System capabilities grouped by category - matches platform capability definitions
// Fallback capability matrix used only if GET /available-capabilities is unreachable.
// The backend capability registry (routers/capabilities.py::AVAILABLE_CAPABILITIES) is the
// source of truth; this static list drifts from it and is kept only as a degraded fallback.
const DEFAULT_CAPABILITY_GROUPS: { label: string; capabilities: string[] }[] = [
  {
    label: 'Core',
    capabilities: [
      'member_management', 'program_management', 'analytics', 'communications',
    ],
  },
  {
    label: 'Engagement',
    capabilities: [
      'geo_tracking', 'survey_feedback', 'incentives_rewards',
    ],
  },
  {
    label: 'Care Coordination',
    capabilities: [
      'care_referrals', 'network_directory', 'tco_service_referrals',
      'tco_workflows', 'tco_ai_recommendations', 'tco_analytics',
    ],
  },
  {
    label: 'Automation & Forms',
    capabilities: [
      'form_builder', 'workflow_automation', 'task_management',
    ],
  },
  {
    label: 'Financial & Payments',
    capabilities: [
      'financial_management', 'payment_processing', 'payment_cards',
      'prepaid_cards',
    ],
  },
  {
    label: 'Documents & Integration',
    capabilities: [
      'document_management', 'integration_management', 'translation_management',
    ],
  },
  {
    label: 'AI & Intelligence',
    capabilities: [
      'ai_assistance', 'weavein_platform',
    ],
  },
  {
    label: 'Employer Platform',
    capabilities: [
      'employer_platform', 'employer_employee_portal', 'employer_navigation_sessions',
      'employer_recommendations', 'employer_analytics', 'employer_hris_integration',
    ],
  },
  {
    label: 'Medicaid',
    capabilities: [
      'medicaid_work_requirements', 'medicaid_compliance',
      'medicaid_revenue_cycle', 'medicaid_enrollment',
    ],
  },
  {
    label: 'Workforce',
    capabilities: [
      'workforce_development',
    ],
  },
];

// Human-friendly labels for capability categories returned by the backend registry.
const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core',
  engagement: 'Engagement',
  care: 'Care Coordination',
  care_coordination: 'Care Coordination',
  automation: 'Automation & Forms',
  forms: 'Automation & Forms',
  financial: 'Financial & Payments',
  payments: 'Financial & Payments',
  documents: 'Documents & Integration',
  integration: 'Documents & Integration',
  ai: 'AI & Intelligence',
  insights: 'Insights & Analytics',
  employer: 'Employer Platform',
  medicaid: 'Medicaid',
  workforce: 'Workforce',
  other: 'Other',
};

interface AvailableCapability {
  name: string;
  label: string;
  description: string;
  category: string;
  always_enabled: boolean;
}

async function fetchPlatformAPI<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = { 'Content-Type': 'application/json', ...getAuthHeaders(), ...options.headers };
  const res = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${path}`, { ...options, headers });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
  return res.json();
}

export default function TierConfigurator() {
  const [tiers, setTiers] = useState<PlanTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<TierFormData>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [previewUsers, setPreviewUsers] = useState(10);
  const [previewMembers, setPreviewMembers] = useState(100);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    capabilities: true,
    features: false,
    volume: false,
    credits: true,
  });
  // Capability matrix, sourced from the backend registry (source of truth) with a
  // static fallback if the endpoint is unreachable.
  const [capabilityGroups, setCapabilityGroups] = useState(DEFAULT_CAPABILITY_GROUPS);

  const loadCapabilities = useCallback(async () => {
    try {
      const data = await fetchPlatformAPI<{ by_category: Record<string, AvailableCapability[]> }>(
        '/available-capabilities',
      );
      if (data?.by_category && Object.keys(data.by_category).length > 0) {
        const groups = Object.entries(data.by_category).map(([category, caps]) => ({
          label: CATEGORY_LABELS[category] || category,
          capabilities: caps.map(c => c.name),
        }));
        if (groups.length > 0) setCapabilityGroups(groups);
      }
    } catch {
      // Keep the static fallback on any error — the matrix must still render.
    }
  }, []);

  const loadTiers = useCallback(async () => {
    try {
      setLoading(true);
      // Single plan catalog = shared templates (assignable to orgs + client-visible).
      const data = await fetchPlatformAPI<{ shared_tiers: PlanTier[] }>('/shared-tiers');
      setTiers(data.shared_tiers || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTiers(); }, [loadTiers]);
  useEffect(() => { loadCapabilities(); }, [loadCapabilities]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingTier(null);
    setFormData({ ...EMPTY_FORM, display_order: tiers.length });
  };

  const handleEdit = (tier: PlanTier) => {
    setEditingTier(tier.id);
    setIsCreating(false);
    setFormData({
      tier_code: tier.tier_code,
      tier_name: tier.tier_name,
      description: tier.description || '',
      pricing_model: tier.pricing_model || 'per_seat',
      base_monthly_price: tier.base_monthly_price,
      per_user_price: tier.per_user_price,
      per_member_price: tier.per_member_price ?? 0,
      annual_discount_percent: tier.annual_discount_percent,
      setup_fee: tier.setup_fee || 0,
      billing_interval: tier.billing_interval || 'monthly',
      revenue_recognition: tier.revenue_recognition || 'monthly',
      min_users: tier.min_users,
      max_users: tier.max_users,
      pricing_tiers: tier.pricing_tiers || [],
      revenue_share_config: tier.revenue_share_config || { ...EMPTY_REVENUE_SHARE },
      trial_days: tier.trial_days || 0,
      min_contract_months: tier.min_contract_months || 0,
      capabilities: tier.capabilities || [],
      features: tier.features || [],
      included_communication_credits: tier.included_communication_credits,
      included_ai_credits: tier.included_ai_credits,
      volume_discounts: tier.volume_discounts || [],
      display_order: tier.display_order,
      is_popular: tier.is_popular,
      badge_text: tier.badge_text || '',
      is_active: tier.is_active,
      is_default: tier.is_default,
    });
  };

  const handleCancel = () => {
    setEditingTier(null);
    setIsCreating(false);
    setFormData({ ...EMPTY_FORM });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreating) {
        await fetchPlatformAPI('/shared-tiers', { method: 'POST', body: JSON.stringify(formData) });
      } else if (editingTier) {
        await fetchPlatformAPI(`/shared-tiers/${editingTier}`, { method: 'PUT', body: JSON.stringify(formData) });
      }
      await loadTiers();
      handleCancel();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tierId: string) => {
    if (!confirm('Are you sure you want to archive this plan tier?')) return;
    try {
      await fetchPlatformAPI(`/shared-tiers/${tierId}`, { method: 'DELETE' });
      await loadTiers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const toggleCapability = (cap: string) => {
    const existing = formData.capabilities.find(c => c.capability === cap);
    if (existing) {
      setFormData(prev => ({
        ...prev,
        capabilities: prev.capabilities.map(c =>
          c.capability === cap ? { ...c, enabled: !c.enabled } : c
        ),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        capabilities: [...prev.capabilities, { capability: cap, enabled: true }],
      }));
    }
  };

  const addVolumeDiscount = () => {
    setFormData(prev => ({
      ...prev,
      volume_discounts: [...prev.volume_discounts, { min_users: 50, discount_percent: 10 }],
    }));
  };

  const removeVolumeDiscount = (index: number) => {
    setFormData(prev => ({
      ...prev,
      volume_discounts: prev.volume_discounts.filter((_, i) => i !== index),
    }));
  };

  // Revenue share preview state
  const [previewRevenue, setPreviewRevenue] = useState({ reimbursement: 50000, grant: 0, employer: 0, other: 0 });

  // Pricing calculator — handles all models
  const calculatePrice = (tier: TierFormData, users: number, members: number = 0) => {
    const model = tier.pricing_model || 'per_seat';
    const isAnnual = tier.billing_interval === 'annual';
    let monthlyPrice = 0;
    let revenueShareAmount = 0;

    // Base amount (flat_fee, per_seat, pmpm, hybrid). For annual plans the base
    // IS the annual fee, so the monthly-equivalent is base/12.
    if (['flat_fee', 'per_seat', 'pmpm', 'hybrid'].includes(model)) {
      monthlyPrice += isAnnual ? tier.base_monthly_price / 12 : tier.base_monthly_price;
    }

    // Per-staff seat (per_seat, hybrid)
    if (['per_seat', 'hybrid'].includes(model)) {
      monthlyPrice += users * tier.per_user_price;
    }

    // Per-member (pmpm, hybrid) — recurring on active members
    if (['pmpm', 'hybrid'].includes(model)) {
      monthlyPrice += members * (tier.per_member_price || 0);
    }

    // Tiered pricing
    if (model === 'tiered' && tier.pricing_tiers.length > 0) {
      let remaining = users;
      const sorted = [...tier.pricing_tiers].sort((a, b) => a.from_qty - b.from_qty);
      for (const bracket of sorted) {
        const bracketSize = bracket.to_qty ? Math.min(remaining, bracket.to_qty - bracket.from_qty + 1) : remaining;
        if (bracketSize <= 0) break;
        monthlyPrice += bracket.flat_price > 0 ? bracket.flat_price : bracket.unit_price * bracketSize;
        remaining -= bracketSize;
      }
    }

    // Revenue share (revenue_share, hybrid)
    if (['revenue_share', 'hybrid'].includes(model) && tier.revenue_share_config) {
      const rc = tier.revenue_share_config;
      const raw = (
        previewRevenue.reimbursement * (rc.reimbursement_share_percent || 0) / 100
        + previewRevenue.grant * (rc.grant_share_percent || 0) / 100
        + previewRevenue.employer * (rc.employer_share_percent || 0) / 100
        + previewRevenue.other * (rc.other_share_percent || 0) / 100
      );
      revenueShareAmount = Math.max(raw, rc.minimum_monthly_fee || 0);
      if (rc.cap_monthly_fee != null) revenueShareAmount = Math.min(revenueShareAmount, rc.cap_monthly_fee);
    }

    // Volume discount (per_seat, hybrid)
    if (['per_seat', 'hybrid'].includes(model) && tier.volume_discounts.length > 0) {
      const applicable = [...tier.volume_discounts]
        .sort((a, b) => b.min_users - a.min_users)
        .find(d => users >= d.min_users);
      if (applicable) {
        monthlyPrice *= (1 - applicable.discount_percent / 100);
      }
    }

    const totalMonthly = monthlyPrice + revenueShareAmount;
    return {
      monthly: totalMonthly,
      annual: totalMonthly * 12 * (1 - (tier.annual_discount_percent || 0) / 100),
      revenueShare: revenueShareAmount,
      baseAndSeat: monthlyPrice,
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-indigo-600 animate-spin mr-2" />
        <span className="text-gray-600">Loading plan tiers...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Plan Tiers</h2>
          <p className="text-sm text-gray-500">Configure subscription tiers for client organizations</p>
        </div>
        <button
          onClick={handleCreate}
          disabled={isCreating}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Add Tier
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tier Cards Grid */}
      {!isCreating && !editingTier && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiers.map(tier => (
            <div
              key={tier.id}
              className={`border rounded-xl p-5 relative ${
                tier.is_popular ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-gray-200'
              } ${!tier.is_active ? 'opacity-60' : ''}`}
            >
              {tier.badge_text && (
                <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
                  {tier.badge_text}
                </span>
              )}

              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{tier.tier_name}</h3>
                  <p className="text-xs text-gray-500">{tier.tier_code}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(tier)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(tier.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {tier.description && (
                <p className="text-sm text-gray-600 mb-3">{tier.description}</p>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Model</span>
                  <span className="font-medium capitalize">{(tier.pricing_model || 'per_seat').replace('_', ' ')}</span>
                </div>
                {['flat_fee', 'per_seat', 'hybrid'].includes(tier.pricing_model || 'per_seat') && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{tier.billing_interval === 'annual' ? 'Annual Fee' : 'Base Price'}</span>
                    <span className="font-medium">${tier.base_monthly_price}{tier.billing_interval === 'annual' ? '/yr' : '/mo'}</span>
                  </div>
                )}
                {['per_seat', 'hybrid'].includes(tier.pricing_model || 'per_seat') && tier.per_user_price > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Per Staff</span>
                    <span className="font-medium">${tier.per_user_price}/staff/mo</span>
                  </div>
                )}
                {['pmpm', 'hybrid'].includes(tier.pricing_model || '') && (tier.per_member_price || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Per Member</span>
                    <span className="font-medium">${tier.per_member_price}/member/mo</span>
                  </div>
                )}
                {['revenue_share', 'hybrid'].includes(tier.pricing_model || '') && tier.revenue_share_config?.reimbursement_share_percent > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Revenue Share</span>
                    <span className="font-medium text-amber-600">{tier.revenue_share_config.reimbursement_share_percent}% reimb.</span>
                  </div>
                )}
                {tier.pricing_model === 'tiered' && tier.pricing_tiers?.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tiers</span>
                    <span className="font-medium">{tier.pricing_tiers.length} brackets</span>
                  </div>
                )}
                {tier.annual_discount_percent > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Annual Discount</span>
                    <span className="font-medium text-green-600">{tier.annual_discount_percent}%</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Users</span>
                  <span className="font-medium">
                    {tier.min_users} - {tier.max_users || 'Unlimited'}
                  </span>
                </div>
                {tier.included_communication_credits > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Comm Credits</span>
                    <span className="font-medium">${tier.included_communication_credits}</span>
                  </div>
                )}
                {tier.included_ai_credits > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">AI Credits</span>
                    <span className="font-medium">${tier.included_ai_credits}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs">
                {tier.is_active ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Active</span>
                ) : (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">Archived</span>
                )}
                {tier.is_default && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Default</span>
                )}
                <span className="text-gray-400 ml-auto">
                  {(tier.capabilities || []).filter(c => c.enabled).length} capabilities
                </span>
              </div>
            </div>
          ))}

          {tiers.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No plan tiers configured yet.</p>
              <p className="text-sm">Click "Add Tier" to create your first plan tier.</p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Form */}
      {(isCreating || editingTier) && (
        <div className="border rounded-xl p-6 bg-white space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {isCreating ? 'Create Plan Tier' : 'Edit Plan Tier'}
            </h3>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tier Code</label>
              <input
                type="text"
                value={formData.tier_code}
                onChange={e => setFormData(prev => ({ ...prev, tier_code: e.target.value }))}
                placeholder="e.g., starter"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tier Name</label>
              <input
                type="text"
                value={formData.tier_name}
                onChange={e => setFormData(prev => ({ ...prev, tier_name: e.target.value }))}
                placeholder="e.g., Starter"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Pricing Model Selector */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4" /> Pricing Model
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {PRICING_MODEL_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, pricing_model: opt.value }))}
                  className={`p-3 border rounded-lg text-left text-sm transition-colors ${
                    formData.pricing_model === opt.value
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-500'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">{opt.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.description}</div>
                </button>
              ))}
            </div>

            {/* Base Pricing Fields (flat_fee, per_seat, hybrid) */}
            {['flat_fee', 'per_seat', 'hybrid'].includes(formData.pricing_model) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    {formData.billing_interval === 'annual' ? 'Annual Fee ($)' : 'Base Monthly ($)'}
                  </label>
                  <input type="number" value={formData.base_monthly_price}
                    onChange={e => setFormData(prev => ({ ...prev, base_monthly_price: parseFloat(e.target.value) || 0 }))}
                    min={0} step={0.01} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                {['per_seat', 'hybrid'].includes(formData.pricing_model) && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Per Staff (seat)/mo ($)</label>
                    <input type="number" value={formData.per_user_price}
                      onChange={e => setFormData(prev => ({ ...prev, per_user_price: parseFloat(e.target.value) || 0 }))}
                      min={0} step={0.01} className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                )}
                {['pmpm', 'hybrid'].includes(formData.pricing_model) && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Per Member/mo ($)</label>
                    <input type="number" value={formData.per_member_price}
                      onChange={e => setFormData(prev => ({ ...prev, per_member_price: parseFloat(e.target.value) || 0 }))}
                      min={0} step={0.01} className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Annual Discount (%)</label>
                  <input type="number" value={formData.annual_discount_percent}
                    onChange={e => setFormData(prev => ({ ...prev, annual_discount_percent: parseFloat(e.target.value) || 0 }))}
                    min={0} max={100} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Setup Fee ($)</label>
                  <input type="number" value={formData.setup_fee}
                    onChange={e => setFormData(prev => ({ ...prev, setup_fee: parseFloat(e.target.value) || 0 }))}
                    min={0} step={0.01} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>
            )}

            {/* Tiered Pricing Brackets */}
            {formData.pricing_model === 'tiered' && (
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600">Pricing Tiers</label>
                  <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    pricing_tiers: [...prev.pricing_tiers, { from_qty: prev.pricing_tiers.length > 0 ? (prev.pricing_tiers[prev.pricing_tiers.length - 1].to_qty || 0) + 1 : 1, to_qty: null, unit_price: 0, flat_price: 0 }],
                  }))} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add Bracket
                  </button>
                </div>
                {formData.pricing_tiers.map((bracket, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-2 items-end">
                    <div>
                      <label className="block text-xs text-gray-500">From</label>
                      <input type="number" value={bracket.from_qty}
                        onChange={e => { const tiers = [...formData.pricing_tiers]; tiers[idx] = { ...tiers[idx], from_qty: parseInt(e.target.value) || 0 }; setFormData(prev => ({ ...prev, pricing_tiers: tiers })); }}
                        className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">To (blank=∞)</label>
                      <input type="number" value={bracket.to_qty ?? ''}
                        onChange={e => { const tiers = [...formData.pricing_tiers]; tiers[idx] = { ...tiers[idx], to_qty: e.target.value ? parseInt(e.target.value) : null }; setFormData(prev => ({ ...prev, pricing_tiers: tiers })); }}
                        className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">$/unit</label>
                      <input type="number" value={bracket.unit_price}
                        onChange={e => { const tiers = [...formData.pricing_tiers]; tiers[idx] = { ...tiers[idx], unit_price: parseFloat(e.target.value) || 0 }; setFormData(prev => ({ ...prev, pricing_tiers: tiers })); }}
                        min={0} step={0.01} className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Flat ($)</label>
                      <input type="number" value={bracket.flat_price}
                        onChange={e => { const tiers = [...formData.pricing_tiers]; tiers[idx] = { ...tiers[idx], flat_price: parseFloat(e.target.value) || 0 }; setFormData(prev => ({ ...prev, pricing_tiers: tiers })); }}
                        min={0} step={0.01} className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, pricing_tiers: prev.pricing_tiers.filter((_, i) => i !== idx) }))}
                      className="p-1.5 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Revenue Share Config */}
            {['revenue_share', 'hybrid'].includes(formData.pricing_model) && (
              <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
                <h5 className="text-sm font-medium text-amber-800 dark:text-amber-300">Revenue Share Terms</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Reimbursement %</label>
                    <input type="number" value={formData.revenue_share_config.reimbursement_share_percent}
                      onChange={e => setFormData(prev => ({ ...prev, revenue_share_config: { ...prev.revenue_share_config, reimbursement_share_percent: parseFloat(e.target.value) || 0 } }))}
                      min={0} max={100} step={0.1} className="w-full px-2 py-1.5 border rounded text-sm" />
                    <span className="text-xs text-gray-400">Medicaid/insurance</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Grant %</label>
                    <input type="number" value={formData.revenue_share_config.grant_share_percent}
                      onChange={e => setFormData(prev => ({ ...prev, revenue_share_config: { ...prev.revenue_share_config, grant_share_percent: parseFloat(e.target.value) || 0 } }))}
                      min={0} max={100} step={0.1} className="w-full px-2 py-1.5 border rounded text-sm" />
                    <span className="text-xs text-gray-400">Grant funding</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Employer %</label>
                    <input type="number" value={formData.revenue_share_config.employer_share_percent}
                      onChange={e => setFormData(prev => ({ ...prev, revenue_share_config: { ...prev.revenue_share_config, employer_share_percent: parseFloat(e.target.value) || 0 } }))}
                      min={0} max={100} step={0.1} className="w-full px-2 py-1.5 border rounded text-sm" />
                    <span className="text-xs text-gray-400">Employer contracts</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Other %</label>
                    <input type="number" value={formData.revenue_share_config.other_share_percent}
                      onChange={e => setFormData(prev => ({ ...prev, revenue_share_config: { ...prev.revenue_share_config, other_share_percent: parseFloat(e.target.value) || 0 } }))}
                      min={0} max={100} step={0.1} className="w-full px-2 py-1.5 border rounded text-sm" />
                    <span className="text-xs text-gray-400">Other sources</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Minimum Monthly ($)</label>
                    <input type="number" value={formData.revenue_share_config.minimum_monthly_fee}
                      onChange={e => setFormData(prev => ({ ...prev, revenue_share_config: { ...prev.revenue_share_config, minimum_monthly_fee: parseFloat(e.target.value) || 0 } }))}
                      min={0} step={1} className="w-full px-2 py-1.5 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Monthly Cap ($, blank=none)</label>
                    <input type="number" value={formData.revenue_share_config.cap_monthly_fee ?? ''}
                      onChange={e => setFormData(prev => ({ ...prev, revenue_share_config: { ...prev.revenue_share_config, cap_monthly_fee: e.target.value ? parseFloat(e.target.value) : null } }))}
                      min={0} step={1} className="w-full px-2 py-1.5 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Reconciliation</label>
                    <select value={formData.revenue_share_config.reconciliation_frequency}
                      onChange={e => setFormData(prev => ({ ...prev, revenue_share_config: { ...prev.revenue_share_config, reconciliation_frequency: e.target.value } }))}
                      className="w-full px-2 py-1.5 border rounded text-sm">
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Contract Terms */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Billing Interval</label>
                <select value={formData.billing_interval}
                  onChange={e => setFormData(prev => ({ ...prev, billing_interval: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm">
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual (prepay)</option>
                </select>
                {formData.billing_interval === 'annual' && (
                  <p className="mt-1 text-[11px] text-gray-400">Base price is the annual amount, invoiced once.</p>
                )}
              </div>
              {formData.billing_interval === 'annual' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Revenue Recognition</label>
                  <select value={formData.revenue_recognition || 'monthly'}
                    onChange={e => setFormData(prev => ({ ...prev, revenue_recognition: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="monthly">Monthly (spread over 12)</option>
                    <option value="upfront">Upfront</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Trial Days</label>
                <input type="number" value={formData.trial_days}
                  onChange={e => setFormData(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 0 }))}
                  min={0} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Contract (months)</label>
                <input type="number" value={formData.min_contract_months}
                  onChange={e => setFormData(prev => ({ ...prev, min_contract_months: parseInt(e.target.value) || 0 }))}
                  min={0} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Display Order</label>
                <input type="number" value={formData.display_order}
                  onChange={e => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  min={0} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
          </div>

          {/* User Limits */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> User Limits
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Min Users</label>
                <input
                  type="number"
                  value={formData.min_users}
                  onChange={e => setFormData(prev => ({ ...prev, min_users: parseInt(e.target.value) || 1 }))}
                  min={1}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Users (blank = unlimited)</label>
                <input
                  type="number"
                  value={formData.max_users ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, max_users: e.target.value ? parseInt(e.target.value) : null }))}
                  min={1}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
          </div>

          {/* Included Credits */}
          <div>
            <button onClick={() => toggleSection('credits')} className="flex items-center gap-2 font-medium text-gray-900 w-full">
              {expandedSections.credits ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <Zap className="w-4 h-4" /> Included Credits
            </button>
            {expandedSections.credits && (
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    <MessageSquare className="w-3 h-3 inline mr-1" />
                    Communication Credits ($)
                  </label>
                  <input
                    type="number"
                    value={formData.included_communication_credits}
                    onChange={e => setFormData(prev => ({ ...prev, included_communication_credits: parseFloat(e.target.value) || 0 }))}
                    min={0} step={1}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    <Bot className="w-3 h-3 inline mr-1" />
                    AI Token Credits ($)
                  </label>
                  <input
                    type="number"
                    value={formData.included_ai_credits}
                    onChange={e => setFormData(prev => ({ ...prev, included_ai_credits: parseFloat(e.target.value) || 0 }))}
                    min={0} step={1}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Capabilities */}
          <div>
            <button onClick={() => toggleSection('capabilities')} className="flex items-center gap-2 font-medium text-gray-900 w-full">
              {expandedSections.capabilities ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Capabilities ({formData.capabilities.filter(c => c.enabled).length} selected)
            </button>
            {expandedSections.capabilities && (
              <div className="mt-3 space-y-4">
                {capabilityGroups.map(group => (
                  <div key={group.label}>
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{group.label}</h5>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {group.capabilities.map(cap => {
                        const enabled = formData.capabilities.find(c => c.capability === cap)?.enabled ?? false;
                        return (
                          <label key={cap} className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={() => toggleCapability(cap)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700 capitalize">{cap.replace(/_/g, ' ')}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Volume Discounts */}
          <div>
            <button onClick={() => toggleSection('volume')} className="flex items-center gap-2 font-medium text-gray-900 w-full">
              {expandedSections.volume ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Volume Discounts
            </button>
            {expandedSections.volume && (
              <div className="space-y-2 mt-3">
                {formData.volume_discounts.map((vd, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-sm text-gray-500 whitespace-nowrap">When users &ge;</span>
                    <input
                      type="number"
                      value={vd.min_users}
                      onChange={e => {
                        const updated = [...formData.volume_discounts];
                        updated[idx] = { ...updated[idx], min_users: parseInt(e.target.value) || 0 };
                        setFormData(prev => ({ ...prev, volume_discounts: updated }));
                      }}
                      min={1}
                      className="w-24 px-2 py-1.5 border rounded text-sm"
                    />
                    <span className="text-sm text-gray-500">discount</span>
                    <input
                      type="number"
                      value={vd.discount_percent}
                      onChange={e => {
                        const updated = [...formData.volume_discounts];
                        updated[idx] = { ...updated[idx], discount_percent: parseFloat(e.target.value) || 0 };
                        setFormData(prev => ({ ...prev, volume_discounts: updated }));
                      }}
                      min={0} max={100}
                      className="w-20 px-2 py-1.5 border rounded text-sm"
                    />
                    <span className="text-sm text-gray-500">%</span>
                    <button onClick={() => removeVolumeDiscount(idx)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button onClick={addVolumeDiscount} className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add Volume Discount
                </button>
              </div>
            )}
          </div>

          {/* Display Options */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_popular}
                onChange={e => setFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm">Mark as Popular</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={e => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm">Default Tier</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600"
              />
              <span className="text-sm">Active</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Badge:</label>
              <input
                type="text"
                value={formData.badge_text}
                onChange={e => setFormData(prev => ({ ...prev, badge_text: e.target.value }))}
                placeholder="e.g., Most Popular"
                className="px-2 py-1 border rounded text-sm w-36"
              />
            </div>
          </div>

          {/* Pricing Preview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Calculator className="w-4 h-4" /> Pricing Preview
            </h4>
            <div className="flex items-center gap-4 mb-3">
              <label className="text-sm text-gray-500">Users:</label>
              <input
                type="range"
                min={formData.min_users}
                max={formData.max_users || 200}
                value={previewUsers}
                onChange={e => setPreviewUsers(parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-medium w-12 text-right">{previewUsers}</span>
            </div>
            {['pmpm', 'hybrid'].includes(formData.pricing_model) && (
              <div className="flex items-center gap-3 mb-3">
                <label className="text-sm text-gray-500">Members:</label>
                <input
                  type="range"
                  min={0}
                  max={2000}
                  step={10}
                  value={previewMembers}
                  onChange={e => setPreviewMembers(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-12 text-right">{previewMembers}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Monthly</p>
                <p className="text-xl font-bold text-gray-900">
                  ${calculatePrice(formData, previewUsers, previewMembers).monthly.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Annual (total)</p>
                <p className="text-xl font-bold text-gray-900">
                  ${calculatePrice(formData, previewUsers, previewMembers).annual.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.tier_code || !formData.tier_name}
              className="inline-flex items-center px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {isCreating ? 'Create Tier' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
